import { useFaroSetUser } from 'common-rum';

import { LoginLogoutState, useLoginLogout } from './login-logout-state';
import { User } from './user';
import { UserState, useUserState } from './user-state';

export type AuthState = (
  | {
      name: 'checking if logged in';
    }
  | {
      name: 'logged out';
      error: unknown;
    }
  | {
      name: 'logging in';
    }
  | {
      name: 'logged in';
      user: User;
    }
) & {
  invalidate: UserState['invalidate'];
  logIn: LoginLogoutState['logIn'];
  logOut: LoginLogoutState['logOut'];
  update: UserState['update'];
};

export function useAuthState({
  getUser,
  getSsoToken,
  on,
}: {
  getUser: Parameters<typeof useUserState>[0]['getUser'];
  getSsoToken: Parameters<typeof useLoginLogout>[0]['getSsoToken'];
  on: Parameters<typeof useUserState>[0]['on'] &
    Parameters<typeof useLoginLogout>[0]['on'];
}): AuthState {
  const { setUser } = useFaroSetUser();
  const userState = useUserState({
    getUser,
    on,
    setUser,
  });
  const loginLogoutState = useLoginLogout({
    userState,
    getSsoToken,
    on,
  });

  return userState.name === 'success'
    ? {
        name: 'logged in',
        user: userState.user,
        invalidate: userState.invalidate,
        logIn: loginLogoutState.logIn,
        logOut: loginLogoutState.logOut,
        update: userState.update,
      }
    : userState.name === 'loading'
    ? {
        name: 'checking if logged in',
        invalidate: userState.invalidate,
        logIn: loginLogoutState.logIn,
        logOut: loginLogoutState.logOut,
        update: userState.update,
      }
    : loginLogoutState.name === 'logging in'
    ? {
        name: 'logging in',
        invalidate: userState.invalidate,
        logIn: loginLogoutState.logIn,
        logOut: loginLogoutState.logOut,
        update: userState.update,
      }
    : {
        name: 'logged out',
        invalidate: userState.invalidate,
        logIn: loginLogoutState.logIn,
        logOut: loginLogoutState.logOut,
        update: userState.update,
        error: loginLogoutState.error,
      };
}
